import { ResponseCache } from "../../util/response-cache.js";
import { WIND_VECTOR_BYTES_PER_ELEMENT, WindFieldWorkerNotificationsAPIFactory, distributionPositionIterator, } from "./windfield.js";
import { authenticateRequestHandler, enableLoggingRequestHandler, getRequestHandler, isLoggingEnabled, } from "./worker.js";
import { API } from "../../api/api.js";
import { scalarFieldProducer } from "../../scalarfield/bin2sf.js";
import { WindVectorFactory } from "./wind-vector-factory.js";
import { ScalarFieldSequenceImpl } from "../../scalarfield/implementations.js";
import { Datum, WebMercator, TransverseMercator, } from "../../map/projection.js";
const notificationApi = WindFieldWorkerNotificationsAPIFactory(self);
const log = (content) => {
    notificationApi.log("W " + content);
};
//a map of all the tiles that we need to serve
let tiles = new Map();
//the zoom level we need to serves. We use this
//for filtering tiles since removing tiles is done
//after the signal that the tile layer has been loaded
let zoom = undefined;
//the number of pixels on the WebMercator projection
let resolution;
let windFields;
let uSequence = new ScalarFieldSequenceImpl();
let vSequence = new ScalarFieldSequenceImpl();
const datum = TransverseMercator.getDefaultDatum();
let gridProperties = undefined;
let utm;
let distribution;
let step = undefined;
const color = "#0285bb";
const fontSize = 12;
const maxChars = 4;
const cellSize = 100;
const maxArrow = 1.0; //between 0.0 and 1.0; 0.5 or lower means inside the cell; 0 always minimum
const windLow = 1.0;
const windHigh = 10.0;
const lightnessTrackLength = 0.5;
const wsZeroThreshold = 0.004;
const detailLevel = 4;
const wvf = WindVectorFactory(color, fontSize, maxChars, cellSize, maxArrow, windLow, windHigh, lightnessTrackLength);
const listeners = {
    authenticate: authenticateRequestHandler,
    enableLogging: enableLoggingRequestHandler,
    init: (send, _transfer, hpc, model) => {
        initialize(hpc, model);
        send({ status: "ok" });
    },
    set: (send, _transfer, profile) => {
        if (isLoggingEnabled())
            log("profile: " + JSON.stringify(profile));
        if (windFields) {
            const updateParameters = profile.map((interval) => {
                return {
                    cacheKey: `${interval.start}_${interval.end}`, //{ start: interval.start, end: interval.end },
                    version: interval.version ?? 0,
                };
            });
            windFields.update(updateParameters);
            send({ status: "ok" });
        }
        else {
            send({ status: "not initialized" });
        }
    },
    getField: (_send, transfer, time, buffer) => {
        const u = uSequence.getScalarField(time);
        const v = vSequence.getScalarField(time);
        if (!buffer) {
            //build the transfer buffer
            const vectorCount = Object.keys(distribution).reduce((count, tileKey) => {
                return count + distribution[tileKey].positions.length;
            }, 0);
            if (isLoggingEnabled())
                log(`Worker vectorCount = ${vectorCount}`);
            //add a extra position for the 'position ofInterest'
            buffer = new ArrayBuffer((vectorCount + 1) * WIND_VECTOR_BYTES_PER_ELEMENT);
        }
        transfer([getRawWindField(u, v, buffer)]);
    },
    getVector: (send, _transfer, time, latLng) => {
        const coord = utm ? utm.toCartesian(latLng.lat, latLng.lng) : undefined;
        let windVector = coord ? {
            time,
            latLng,
            utm: { x: coord[0], y: coord[1] },
            u: undefined,
            v: undefined,
        } : undefined;
        if (windVector && uSequence) {
            const u = uSequence.getScalarField(time);
            const v = vSequence.getScalarField(time);
            if (u && v) {
                const uVal = u.value(windVector.utm.x, windVector.utm.y);
                const vVal = v.value(windVector.utm.x, windVector.utm.y);
                if (uVal !== undefined && vVal !== undefined) {
                    windVector.u = uVal;
                    windVector.v = vVal;
                }
            }
        }
        send(windVector);
    },
    getRenderParameters: (send, transfer) => {
        const parameters = {
            color,
            fontSize,
            maxChars,
            cellSize: { x: cellSize, y: cellSize },
            maxArrow,
            windLow,
            windHigh,
            lightnessTrackLength,
            wsZeroThreshold,
            detailLevel,
        };
        send(parameters);
    },
    addTile: (send, _transfer, tile) => {
        if (!tiles.has(tile.key)) {
            tiles.set(tile.key, tile);
            if (isLoggingEnabled())
                log(`added tile ${tile.key} (${tiles.size})`);
            send("added");
        }
        else {
            send("ok");
        }
    },
    removeTile: (send, _transfer, tile) => {
        if (tiles.has(tile.key)) {
            tiles.delete(tile.key);
            if (isLoggingEnabled())
                log(`removed tile ${tile.key} (${tiles.size})`);
            send("removed");
        }
        else {
            send("ok");
        }
    },
    tilesReady: (send, _transfer, zoomLevel, layerResolution, tileSize) => {
        zoom = zoomLevel;
        resolution = layerResolution;
        if (isLoggingEnabled())
            log(`${tiles.size} Tiles at zoom level ${zoom} are ready. resolution = ${JSON.stringify(resolution)}`);
        buildDistribution();
        send("ok");
    },
};
/**
 * Create the entry point of the worker and export it. The lib's index.ts could then
 * export it like:
 *
 * This enables external packagers to create Web Workers. For example when the legacy
 * code base (so a web-pack example) wants to use this implementation the have to create a TS file:
 *
 * file: windfield-worker.ts
 * import { windFieldRequestHandler } from '@mapp/r-mapp-lib';
 * self.onmessage = windFieldRequestHandler;
 * export {};
 *
 * And in the file where you want to use the WindField Web Worker:
 *
 * import MyFancyWorkerFromAModule from 'worker-loader!./path-to/windfield-worker';
 * const worker = new MyFancyWorkerFromAModule();
 *

 */
export const requestHandler = getRequestHandler(this, listeners);
//assign it to self.onmessage
self.onmessage = requestHandler;
/**
 * This method checks if we need this tile and if it does assigns
 * the positions for the wind vectors to display.
 * @param tile the tile to check
 * @param positions the available wind vector positions for current zoom level
 * @param distribution  a map with tiles and their assigned wind vector positions
 */
const checkTile = (tile, positions, distribution) => {
    if (zoom !== undefined && gridProperties !== undefined) {
        if (tile.coords.z !== zoom) {
            //wrong zoom level -> delete the tile
            tiles.delete(tile.key);
            if (isLoggingEnabled())
                log(`pruned tile ${tile.key} (${tiles.size}) : different zoom level`);
        }
        else if (tile.bounds.northEast.lat <=
            gridProperties.geodeticBounds.southWest.lat ||
            tile.bounds.southWest.lat >=
                gridProperties.geodeticBounds.northEast.lat ||
            tile.bounds.northEast.lng <=
                gridProperties.geodeticBounds.southWest.lng ||
            tile.bounds.southWest.lng >= gridProperties.geodeticBounds.northEast.lng) {
            //outside bounds of the wind field -> delete the tile
            tiles.delete(tile.key);
            if (isLoggingEnabled())
                log(`pruned tile ${tile.key} (${tiles.size}) : outside wind field`);
        }
        else {
            //we need this tile
            //find all positions that can be served by this tile (should be four positions as a maximum)
            const positionsOnTile = positions.filter((position) => position.latLng.lat <= tile.bounds.northEast.lat &&
                position.latLng.lat > tile.bounds.southWest.lat &&
                position.latLng.lng < tile.bounds.northEast.lng &&
                position.latLng.lng >= tile.bounds.southWest.lng);
            if (positionsOnTile.length > 0) {
                const origin = {
                    x: tile.coords.x * tile.size.x,
                    y: tile.coords.y * tile.size.y,
                };
                distribution[tile.key] = {
                    tile,
                    positions: positionsOnTile.map((p) => {
                        const point = WebMercator.latLngToPoint(p.latLng, resolution);
                        return {
                            latLng: p.latLng,
                            utm: p.utm,
                            point,
                            //for these svg tiles, y=0 lies at the bottom of the tile
                            tile: {
                                x: point.x - origin.x,
                                y: tile.viewBox.height - (point.y - origin.y),
                            },
                            index: p.index,
                            removed: false,
                        };
                    }),
                };
            }
        }
    }
};
/**
 * Connects the visible tiles to the visible wind vector positions
 */
const buildDistribution = () => {
    if (zoom !== undefined && resolution !== undefined && gridProperties) {
        //determine the size of the grid in EPSG3857 for current zoomLevel / resolution
        const tr = WebMercator.latLngToPoint(gridProperties.geodeticBounds.northEast, resolution);
        const bl = WebMercator.latLngToPoint(gridProperties.geodeticBounds.southWest, resolution);
        const gridProjectionBound = {
            x: { min: bl.x, max: tr.x },
            y: { min: tr.y, max: bl.y },
        };
        const gridProjectionSize = {
            x: gridProjectionBound.x.max - gridProjectionBound.x.min,
            y: gridProjectionBound.y.max - gridProjectionBound.y.min,
        };
        const effectiveCount = gridProjectionSize.x < gridProjectionSize.y
            ? gridProperties.nativeCount.x
            : gridProperties.nativeCount.y;
        const effectiveRange = Math.min(gridProjectionSize.x, gridProjectionSize.y);
        let positions = [];
        //when the number of wind vectors that will fit is smaller then the native number of wind vectors this flag is true
        //we could use this flag to signal the user to zoom in further
        let underflow = effectiveRange < cellSize * (effectiveCount - 1);
        if (effectiveRange < cellSize) {
            //draw center wind vector only
            positions.push({
                latLng: gridProperties.gridCenterGeodetic,
                utm: gridProperties.gridCenterUTM,
                index: { x: 0, y: 0 },
            });
            step = { x: 0, y: 0 };
        }
        else {
            //calculate the optimal step size for iterating over the grid;
            const cellCount = Math.floor(effectiveRange / cellSize) + 1;
            step = {
                x: gridProperties.utmRange.x / (cellCount - 1),
                y: gridProperties.utmRange.y / (cellCount - 1),
            };
            //we could force the step size not to be smaller then a certain value
            step.x = Math.max(step.x, gridProperties.nativeStep.x);
            step.y = Math.max(step.y, gridProperties.nativeStep.y);
            //generate the positions
            let ix = 0;
            let iy = 0;
            for (let y = gridProperties.utmBounds.y.min; y <= gridProperties.utmBounds.y.max; y += step.y) {
                ix = 0;
                for (let x = gridProperties.utmBounds.x.min; x <= gridProperties.utmBounds.x.max; x += step.x) {
                    let latLng = utm.toGeodetic([x, y]);
                    positions.push({
                        latLng: { lat: latLng.lat, lng: latLng.lng },
                        utm: { x, y },
                        index: { x: ix, y: iy },
                    });
                    ix++;
                }
                iy++;
            }
        }
        //now match these positions with their tiles
        distribution = {};
        Array.from(tiles.values()).forEach((tile) => checkTile(tile, positions, distribution));
        notificationApi.positions(distribution);
    }
};
const initialize = (hpc, model) => {
    gridProperties = undefined;
    //construct a loader function
    const windFieldLoader = async (cacheKey) => {
        return new Promise(async (resolve, reject) => {
            try {
                const start = parseInt(cacheKey);
                let response = await API.getWindField(hpc, model, start);
                if (response) {
                    const uvFields = scalarFieldProducer.getScalarFields(response);
                    resolve(uvFields);
                }
                else {
                    //empty content
                    resolve(undefined);
                }
            }
            catch (error) {
                //not found
                //other error (could be retried maybe)
                reject(error);
            }
        });
    };
    //construct a notifier function which gets called when the cache has been updated
    const notifier = async (cacheKey) => {
        if (cacheKey) {
            notificationApi.cacheUpdated(cacheKey);
        }
        else {
            let ready = false;
            //construct the U & V ScalarFieldSequences
            const uvFields = Array.from(windFields.data().values())
                .map((cacheValue) => cacheValue.data)
                .filter((field) => field !== undefined);
            if (uvFields.length > 0) {
                const uFields = uvFields.map((uvField) => uvField[0]);
                const vFields = uvFields.map((uvField) => uvField[1]);
                //   //DEBUG
                //   uvFields.filter( (uvField) => uvField[0].timeRange().start === 1726444800000)
                //   .forEach((uvField) => {
                //     let dump = '';
                //     const uField = <BufferedScalarField>uvField[0];
                //     const vField = <BufferedScalarField>uvField[1];
                //     let yPrint = 10;
                //     let xPrint = 10;
                //     for (let y = 0; y<yPrint; y++) {
                //       dump+='\n';
                //       for (let x = 0; x < xPrint; x++) {
                //         let u = uField.nativeValue(x,y);
                //         let v = vField.nativeValue(x,y);
                //         dump += `(${u.toFixed(1).padStart(4,' ')} , ${v.toFixed(1).padStart(4,' ')}) `;
                //       }
                //   }
                //   console.log(dump)
                // });
                uSequence.setScalarFields(uFields);
                vSequence.setScalarFields(vFields);
                if (gridProperties === undefined) {
                    const u = uvFields[0][0];
                    utm = u.projection();
                    const utmHemisphere = utm.hemisphere();
                    const utmZone = utm.zone();
                    const utmBounds = u.utmBounds();
                    const utmMin = { x: utmBounds.x.min, y: utmBounds.y.min };
                    const utmRange = {
                        x: u.utmBounds().x.max - utmMin.x,
                        y: u.utmBounds().y.max - utmMin.y,
                    };
                    u.iterationBounds().y.count;
                    const nativeStep = u.getStepSize();
                    const nativeCount = {
                        x: Math.floor((utmBounds.x.max - utmBounds.x.min) / nativeStep.x) +
                            1,
                        y: Math.floor((utmBounds.y.max - utmBounds.y.min) / nativeStep.y) +
                            1,
                    };
                    const gridNWGeodetic = utm.toGeodetic([
                        utmBounds.x.min,
                        utmBounds.y.max,
                    ]);
                    const gridNEGeodetic = utm.toGeodetic([
                        utmBounds.x.max,
                        utmBounds.y.max,
                    ]);
                    const gridSWGeodetic = utm.toGeodetic([
                        utmBounds.x.min,
                        utmBounds.y.min,
                    ]);
                    const gridSEGeodetic = utm.toGeodetic([
                        utmBounds.x.max,
                        utmBounds.y.min,
                    ]);
                    const gridBoundsNorthGeodetic = Math.max(gridNWGeodetic.lat, gridNEGeodetic.lat);
                    const gridBoundsSouthGeodetic = Math.min(gridSWGeodetic.lat, gridSEGeodetic.lat);
                    const gridBoundsEastGeodetic = Math.max(gridNEGeodetic.lng, gridSEGeodetic.lng);
                    const gridBoundsWestGeodetic = Math.min(gridNWGeodetic.lng, gridSWGeodetic.lng);
                    const geodeticBounds = {
                        southWest: {
                            lat: gridBoundsSouthGeodetic,
                            lng: gridBoundsWestGeodetic,
                        },
                        northEast: {
                            lat: gridBoundsNorthGeodetic,
                            lng: gridBoundsEastGeodetic,
                        },
                    };
                    //we need to be able to take measurements based on geodetic coordinates. We do this
                    //by setting the origin of the datum to the center of the grid
                    const gridCenterUTM = {
                        x: utmBounds.x.min + utmRange.x / 2,
                        y: utmBounds.y.min + utmRange.y / 2,
                    };
                    const gridCenterGeodetic = utm.toGeodetic([
                        gridCenterUTM.x,
                        gridCenterUTM.y,
                    ]);
                    datum.origin(gridCenterGeodetic);
                    gridProperties = {
                        utmHemisphere,
                        utmZone,
                        utmBounds,
                        utmMin,
                        utmRange,
                        gridCenterUTM,
                        gridCenterGeodetic,
                        geodeticBounds,
                        nativeStep,
                        nativeCount,
                    };
                    if (isLoggingEnabled())
                        log("gridProperties: " + JSON.stringify(gridProperties));
                    buildDistribution();
                }
            }
            else {
                uSequence.clear();
                vSequence.clear();
            }
            const timeRange = uSequence.timeRange();
            notificationApi.cacheComplete(timeRange, gridProperties, !!gridProperties);
        }
    };
    //create the cache
    windFields = new ResponseCache(windFieldLoader, notifier);
};
const getRawWindField = (u, v, buffer) => {
    const dataView = new DataView(buffer);
    const uValue = u
        ? (xy) => {
            const val = u.value(xy.x, xy.y);
            return val;
        }
        : (xy) => 0.0;
    const vValue = v
        ? (xy) => {
            const val = v.value(xy.x, xy.y);
            return val;
        }
        : (xy) => 0.0;
    let iBuf = 0;
    for (let position of distributionPositionIterator(distribution)) {
        const uVal = uValue(position.utm);
        const vVal = vValue(position.utm);
        const displayWindSpeed = detailLevel > 0 ?
            position.index.x % detailLevel === 0 &&
                position.index.y % detailLevel === 0 : false;
        wvf.displayWindSpeed(displayWindSpeed);
        const ws = Math.sqrt(uVal * uVal + vVal * vVal);
        const angle = Math.atan2(vVal, uVal);
        const path = wvf.arrowPathCoordinates(ws, angle, position.tile);
        //set the position's index
        dataView.setUint16(iBuf, position.index.x);
        iBuf += Uint16Array.BYTES_PER_ELEMENT;
        dataView.setUint16(iBuf, position.index.y);
        iBuf += Uint16Array.BYTES_PER_ELEMENT;
        //set the wind speed
        dataView.setFloat32(iBuf, ws);
        iBuf += Float32Array.BYTES_PER_ELEMENT;
        //set the color
        wvf.colorAsArray(ws, buffer, iBuf);
        iBuf += 4 * Uint8Array.BYTES_PER_ELEMENT;
        //set the inner color
        wvf.innerColorAsArray(ws, displayWindSpeed, buffer, iBuf);
        iBuf += 4 * Uint8Array.BYTES_PER_ELEMENT;
        //set the path
        dataView.setFloat32(iBuf, path[0]);
        iBuf += Float32Array.BYTES_PER_ELEMENT;
        dataView.setFloat32(iBuf, path[1]);
        iBuf += Float32Array.BYTES_PER_ELEMENT;
        dataView.setFloat32(iBuf, path[2]);
        iBuf += Float32Array.BYTES_PER_ELEMENT;
        dataView.setFloat32(iBuf, path[3]);
        iBuf += Float32Array.BYTES_PER_ELEMENT;
        dataView.setFloat32(iBuf, path[4]);
        iBuf += Float32Array.BYTES_PER_ELEMENT;
        dataView.setFloat32(iBuf, path[5]);
        iBuf += Float32Array.BYTES_PER_ELEMENT;
    }
    return buffer;
};
export {};
